/**
 * P3+ reading practice: `docs/missions/spec.md` (the "Decisions" section, §8, names the unit
 * `Practice` rather than the earlier draft's "Mission", to avoid colliding with the P1 island
 * "mission" already in `docs/minigames/README.md`).
 *
 * Imports only from `primitives.ts`, never from `index.ts` — `index.ts`'s `LessonBundle` needs
 * this module's types, so the reverse import would be a cycle.
 */
import { z } from 'zod';
import { Id, LevelId, LocalisedText, MediaId, SentenceToken } from './primitives.ts';

/** One sentence of a passage: reuses `SentenceToken` so pinyin-on-tap and tap-to-hear are the
 * same mechanism a word/sentence exercise already uses. Every passage sentence is read-mode: all
 * its hanzi must be taught or a sight word, same rule as `Sentence`. */
export const PassageSentence = z.object({
  id: Id,
  text: z.string().min(1),
  tokens: z.array(SentenceToken).min(1),
  /** Which paragraph (0-based) this sentence belongs to, for no-scroll pagination. */
  paragraphIdx: z.number().int().nonnegative(),
  meaningEn: z.string().min(1),
  /** Per-sentence clip: tap-to-hear at the sentence granularity. */
  audioId: MediaId.nullable(),
});
export type PassageSentence = z.infer<typeof PassageSentence>;

export const PassageGenre = z.enum(['narrative', 'informational', 'dialogue', 'message']);
export type PassageGenre = z.infer<typeof PassageGenre>;

export const Passage = z.object({
  id: Id,
  levelId: LevelId,
  lessonId: Id,
  /** Every hanzi in the passage must be taught by this lesson, or a sight word — the same rule
   * `Sentence` uses, just at passage scope. */
  allowedFromLesson: Id,
  title: LocalisedText.nullable(),
  genre: PassageGenre,
  sentences: z.array(PassageSentence).min(1),
  /** Character count of `sentences` joined, checked by the validator against the level's length
   * band (P3 60–120, P4 120–200, P5–P6 200–500 — a warning, not an error). */
  charCount: z.number().int().positive(),
  /** A whole-passage clip for a listening-comprehension variant; null for a reading-only passage
   * (every P3 MVP passage). */
  listeningAudioId: MediaId.nullable(),
});
export type Passage = z.infer<typeof Passage>;

export const PsleSkill = z.enum(['reading', 'listening', 'language_use', 'oral']);
export type PsleSkill = z.infer<typeof PsleSkill>;

/** Reading questions are tagged by cognitive level so a literal question can lead an inference
 * one — not evidence from a study, ordinary reading-comprehension pedagogy. */
export const ReadingLevel = z.enum(['literal', 'inference', 'main_idea']);
export type ReadingLevel = z.infer<typeof ReadingLevel>;

const PracticeQuestionBase = z.object({
  id: Id,
  skillTag: PsleSkill,
  difficulty: z.number().int().min(1).max(5),
  /** Shown only in review tooling, never to the child during play. */
  explanationEn: z.string().min(1),
});

/** Literal / inference / main-idea multiple choice over a `Passage`. */
export const PracticeMcq = PracticeQuestionBase.extend({
  type: z.literal('mcq'),
  passageId: Id,
  readingLevel: ReadingLevel,
  stem: z.string().min(1),
  /** Read the stem aloud; when present the stem may use characters the reading pass hasn't
   * taught, because the child never has to decode it visually (validator rule). */
  stemAudioId: MediaId.nullable(),
  choices: z
    .array(z.object({ id: Id, text: z.string().min(1) }))
    .min(2)
    .max(4),
  answerId: Id,
});
export type PracticeMcq = z.infer<typeof PracticeMcq>;

/**
 * 语文应用: choose the correct word for a blank (P3 MVP: `word_choice` only).
 *
 * Authoring rule (not mechanically checked — a validator can confirm `answerId` is one of
 * `choices`, but not that it is the *only* defensible one): every distractor must be clearly
 * wrong once dropped into `textBefore` + choice + `textAfter` — either ungrammatical Chinese, or
 * grammatical but contradicted by the rest of the passage. A distractor that is merely less
 * likely or less natural than the answer still lets a child argue for it, which breaks first-try
 * scoring honestly. E.g. for "我希望将来___一个司机" the distractors 给/在 fail because
 * "给一个司机"/"在一个司机" are not grammatical Chinese, not merely because 当 reads better; for
 * "现在我要好好学习，___练习" 常常 would have been a bad distractor (常常练习 also works) but
 * 昨天/已经 fail because they contradict the future-tense 要 earlier in the same sentence. See
 * `content/practice/README.md` for the full authoring checklist.
 */
export const PracticeCloze = PracticeQuestionBase.extend({
  type: z.literal('cloze'),
  /** A cloze can stand alone (a language-use drill) or be anchored in a passage/sentence. */
  passageId: Id.nullable(),
  sentenceId: Id.nullable(),
  textBefore: z.string(),
  textAfter: z.string(),
  /** Read the surrounding text aloud, same audio-supported exemption as `PracticeMcq.stem`. */
  textAudioId: MediaId.nullable(),
  choices: z
    .array(z.object({ id: Id, text: z.string().min(1) }))
    .min(2)
    .max(4),
  answerId: Id,
  clozeKind: z.enum(['idiom', 'connective', 'word_choice', 'particle']),
});
export type PracticeCloze = z.infer<typeof PracticeCloze>;

/**
 * Connectives a `cloze` of `clozeKind: 'connective'` may use — both halves of every correlative
 * pair the P4+ language-use drill targets (因为/所以, 虽然/但是, 如果/就, 不但/而且, 一边/一边),
 * plus a few more of the same family. The validator (`lintPractice`) requires every choice on a
 * connective cloze, not just the answer, to be drawn from this list: a distractor that is some
 * other part of speech would fail by being an obviously different kind of word, not by being the
 * wrong connective, which is a weaker test than the authoring rule in `content/practice/README.md`
 * asks for.
 */
export const CONNECTIVES = [
  '因为',
  '所以',
  '虽然',
  '但是',
  '如果',
  '就',
  '不但',
  '而且',
  '一边',
  '只要',
  '除非',
  '即使',
  '不管',
  '还是',
] as const;

/**
 * P4+: put 3-5 sentences (or fragments) of a short paragraph in the right order. `items` is
 * authored *in the correct order* — the player shuffles them for display and scores the child's
 * tap order against this array, so there is nothing else to encode as an "answer". Tap-to-order
 * on a touch screen (drag is optional, per the spec) and first-try scoring, same as every other
 * practice question type.
 */
export const PracticeOrdering = PracticeQuestionBase.extend({
  type: z.literal('ordering'),
  /** An ordering set is usually its own short paragraph, not necessarily anchored in the lesson's
   * main passage — null when it stands alone. */
  passageId: Id.nullable(),
  items: z
    .array(z.object({ id: Id, text: z.string().min(1) }))
    .min(3)
    .max(5),
});
export type PracticeOrdering = z.infer<typeof PracticeOrdering>;

/**
 * P4+: play a clip (a sentence or short paragraph) and answer an MCQ about it — `clipText` is
 * never shown to the child before they answer (the player only reveals it afterwards, as the
 * listening-comprehension teaching moment). Unlike every other audio-carrying field in this file,
 * `audioId` here is not a reading-level *support* for otherwise-visible text — the clip is the
 * whole question, so it must never be silent: `lintPractice` errors when a `listening_mcq` has no
 * recorded clip, rather than letting a TTS fallback stand in for a listening test unnoticed (the
 * `content/practice/README.md` checklist and `gen-audio` cover generating it).
 */
export const PracticeListeningMcq = PracticeQuestionBase.extend({
  type: z.literal('listening_mcq'),
  passageId: Id.nullable(),
  clipText: z.string().min(1),
  audioId: MediaId.nullable(),
  choices: z
    .array(z.object({ id: Id, text: z.string().min(1) }))
    .min(2)
    .max(4),
  answerId: Id,
});
export type PracticeListeningMcq = z.infer<typeof PracticeListeningMcq>;

/** One sentence of a `read_aloud` question: reuses `SentenceToken` for the same tap-to-hear /
 * pinyin-on-tap mechanism a `PassageSentence` uses, since the child reads this text on screen
 * before/while recording it. `audioId` is the model reading, resolved the same way a passage
 * sentence's is (never null once built — TTS is an acceptable model voice, only a *listening*
 * clip like `PracticeListeningMcq.audioId` may never fall back silently). */
export const ReadAloudSentence = z.object({
  id: Id,
  text: z.string().min(1),
  tokens: z.array(SentenceToken).min(1),
  audioId: MediaId.nullable(),
});
export type ReadAloudSentence = z.infer<typeof ReadAloudSentence>;

/**
 * P4+: read one sentence at a time aloud, using the same record → recognise → review flow a
 * `Speak` word exercise uses (`apps/web/src/components/exercises/SpeakPrompt.tsx`), just fed a
 * sentence instead of a word. Per spec §4.1: feedback is honest about what the scorer can and
 * cannot judge — per-syllable tone feedback only where the vendor's response aligns one syllable
 * per character, otherwise a plain completion verdict ("well read" / "try again") from the ASR
 * transcript, and never a passage-level tone-accuracy claim the scorer cannot actually make.
 * 1-3 sentences per question, never a whole passage in one take (§8 decision: read-aloud is
 * single-sentence only).
 */
export const PracticeReadAloud = PracticeQuestionBase.extend({
  type: z.literal('read_aloud'),
  passageId: Id.nullable(),
  sentences: z.array(ReadAloudSentence).min(1).max(3),
});
export type PracticeReadAloud = z.infer<typeof PracticeReadAloud>;

/** Discriminated union: the P3 MVP's `mcq` + `cloze`, plus the P4-P6 types from the spec's §6.1
 * list (`ordering`, `listening_mcq`, `read_aloud`) — `oral_prompt` is still deferred. */
export const PracticeQuestion = z.discriminatedUnion('type', [
  PracticeMcq,
  PracticeCloze,
  PracticeOrdering,
  PracticeListeningMcq,
  PracticeReadAloud,
]);
export type PracticeQuestion = z.infer<typeof PracticeQuestion>;

/** One practice set: a passage plus its questions, attached to a lesson (not a set — a P3+
 * passage's vocabulary usually spans the whole lesson). */
export const Practice = z.object({
  id: Id,
  levelId: LevelId,
  lessonId: Id,
  passageId: Id.nullable(),
  questionIds: z.array(Id).min(1),
  /** Off by default everywhere (P5–P6 only, per spec §8 decision 4); a soft countdown, never a
   * hard cut-off. Always null at the P3 MVP. */
  timerMinutes: z.number().int().positive().nullable(),
});
export type Practice = z.infer<typeof Practice>;

/** Approximate passage length band per level (character count), a content-quality signal for
 * the validator (warning, not an error — the level plan states these as "~" ranges). */
export const PASSAGE_LENGTH_BAND: Partial<Record<LevelId, readonly [number, number]>> = {
  p3: [60, 120],
  p4: [120, 200],
  p5: [200, 500],
  p6: [200, 500],
};

/** One lesson's practice content: `content/practice/<lessonId>.json`. */
export const LessonPractice = z.object({
  lessonId: Id,
  passages: z.array(Passage).default([]),
  questions: z.array(PracticeQuestion).default([]),
  practices: z.array(Practice).default([]),
});
export type LessonPractice = z.infer<typeof LessonPractice>;
